/**
 * EL Header: mega menu dropdowns and mobile drawer.
 * Uses event delegation; sets --header-group-height for theme compatibility.
 */
(function () {
  const getDrawerOverlay = () => {
    let overlay = document.getElementById('el-header-drawer-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'el-header-drawer-overlay';
      overlay.className = 'el-header__drawer-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
      overlay.addEventListener('click', closeDrawer);
    }
    return overlay;
  };

  const openDrawer = () => {
    const header = document.querySelector('.el-header');
    const sectionId = header?.getAttribute('data-section-id');
    const drawer = sectionId ? document.getElementById('el-header-drawer-' + sectionId) : null;
    if (drawer) {
      document.body.classList.add('el-drawer-open');
      getDrawerOverlay().classList.add('is-visible');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      drawer.querySelector('.el-header__drawer-close')?.focus();
    }
  };

  const closeDrawer = () => {
    const drawer = document.querySelector('.el-header__drawer');
    if (drawer) {
      drawer.classList.remove('is-open');
      const overlay = document.getElementById('el-header-drawer-overlay');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.classList.remove('el-drawer-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.querySelector('[data-el-drawer-open]')?.focus();
    }
  };

  const setHeaderGroupHeight = () => {
    const header = document.querySelector('.el-header');
    const headerGroup = document.querySelector('#header-group');
    if (header && headerGroup) {
      let total = 0;
      for (const child of headerGroup.children) {
        if (child instanceof HTMLElement) total += child.offsetHeight;
      }
      document.body.style.setProperty('--header-height', header.offsetHeight + 'px');
      document.body.style.setProperty('--header-group-height', (total || header.offsetHeight) + 'px');
    }
  };

  const initMegas = (header) => {
    const megasWrap = header?.querySelector('.el-header__megas');
    const triggers = header?.querySelectorAll('[data-mega-trigger]');
    if (!megasWrap || !triggers.length) return;

    let overlay = document.getElementById('el-header-mega-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'el-header-mega-overlay';
      overlay.className = 'el-header__mega-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
      overlay.addEventListener('click', () => {
        const h = document.querySelector('.el-header');
        if (!h) return;
        const m = h.querySelector('.el-header__megas');
        if (m) {
          m.querySelectorAll('.el-header__mega.is-open').forEach((el) => el.classList.remove('is-open'));
          m.style.display = 'none';
        }
        h.querySelectorAll('[data-mega-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
        overlay.classList.remove('is-visible');
        document.body.classList.remove('el-mega-open');
      });
    }

    const closeAll = () => {
      megasWrap.querySelectorAll('.el-header__mega.is-open').forEach((m) => m.classList.remove('is-open'));
      megasWrap.style.display = 'none';
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      overlay.classList.remove('is-visible');
      document.body.classList.remove('el-mega-open');
      header.classList.remove('el-mega-open');
    };

    const openMega = (id) => {
      const mega = document.getElementById('el-mega-' + id);
      if (!mega) return;
      closeAll();
      mega.classList.add('is-open');
      megasWrap.style.display = 'block';
      header.classList.add('el-mega-open');
      requestAnimationFrame(() => {
        overlay.classList.add('is-visible');
        document.body.classList.add('el-mega-open');
      });
      const trigger = header.querySelector('[data-mega-trigger="' + id + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };

    header.addEventListener('mouseenter', (e) => {
      const t = e.target.closest('[data-mega-trigger]');
      if (t) openMega(t.getAttribute('data-mega-trigger'));
    }, true);

    header.addEventListener('mouseleave', (e) => {
      const related = e.relatedTarget;
      if (!related) { closeAll(); return; }
      if (header.contains(related)) return;
      /* Close when cursor moves to overlay (page area) so mega disappears when cursor goes away */
      closeAll();
    }, true);

    header.addEventListener('focusin', (e) => {
      const t = e.target.closest('[data-mega-trigger]');
      if (t) openMega(t.getAttribute('data-mega-trigger'));
    }, true);

    header.addEventListener('focusout', (e) => {
      const related = e.relatedTarget;
      if (!related) { closeAll(); return; }
      if (header.contains(related)) return;
      closeAll();
    }, true);

    triggers.forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.preventDefault();
        const id = btn.getAttribute('data-mega-trigger');
        const mega = document.getElementById('el-mega-' + id);
        if (mega?.classList.contains('is-open')) {
          /* Already open (e.g. from hover): keep it open, don't toggle close */
          return;
        }
        openMega(id);
      });
    });
  };

  /**
   * Mobile/tablet drawer accordion: smooth open/close using scrollHeight.
   * Prevents native details/summary toggle and animates max-height. When opening,
   * we set open, allow content to lay out (maxHeight none), measure scrollHeight, then animate.
   */
  const initDrawerAccordions = () => {
    const drawer = document.querySelector('.el-header__drawer');
    if (!drawer) return;
    const detailsList = drawer.querySelectorAll('.el-header__drawer-details');
    detailsList.forEach((details) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.el-header__drawer-details-content');
      if (!summary || !content) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = details.hasAttribute('open');

        if (isOpen) {
          content.style.maxHeight = content.scrollHeight + 'px';
          requestAnimationFrame(() => {
            content.style.maxHeight = '0';
          });
          content.addEventListener('transitionend', () => {
            details.removeAttribute('open');
            content.style.maxHeight = '';
          }, { once: true });
        } else {
          details.setAttribute('open', '');
          content.style.maxHeight = 'none';
          const height = content.scrollHeight;
          content.style.maxHeight = '0';
          requestAnimationFrame(() => {
            content.style.maxHeight = height + 'px';
          });
        }
      });
    });
  };

  const SCROLL_THRESHOLD = 20;
  const MOBILE_TABLET_MAX = 980;

  /** Homepage only: hide mobile search bar after scrolling down */
  const initMobileSearchScroll = (header) => {
    if (!header.classList.contains('el-header--homepage')) return;

    const updateScrolled = () => {
      const isMobileOrTablet = window.innerWidth <= MOBILE_TABLET_MAX;
      if (!isMobileOrTablet) {
        header.classList.remove('el-header--scrolled');
        return;
      }
      if (window.scrollY > SCROLL_THRESHOLD) {
        header.classList.add('el-header--scrolled');
      } else {
        header.classList.remove('el-header--scrolled');
      }
    };

    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    window.addEventListener('resize', updateScrolled);
  };

  /**
   * Keep nav cart bubble in sync when cart updates (e.g. subtract item in drawer).
   * Theme uses 'cart:update' (ThemeEvents.cartUpdate); detail.data.itemCount is the new total.
   */
  const initCartBubbleSync = () => {
    document.addEventListener('cart:update', (e) => {
      const itemCount = e.detail?.data?.itemCount ?? 0;
      document.querySelectorAll('.el-header__action--cart').forEach((action) => {
        let countEl = action.querySelector('.el-header__cart-count');
        if (itemCount > 0) {
          if (!countEl) {
            countEl = document.createElement('span');
            countEl.className = 'el-header__cart-count';
            countEl.setAttribute('aria-hidden', 'true');
            action.appendChild(countEl);
          }
          countEl.textContent = itemCount;
          countEl.style.display = '';
        } else if (countEl) {
          countEl.style.display = 'none';
          countEl.textContent = '0';
        }
      });
    });
  };

  const init = () => {
    const header = document.querySelector('.el-header');
    if (!header) return;

    initMegas(header);
    setHeaderGroupHeight();
    initDrawerAccordions();
    initMobileSearchScroll(header);
    initCartBubbleSync();

    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-el-drawer-open]') || e.target.closest('[data-el-drawer-open]')) openDrawer();
      if (e.target.matches('[data-el-drawer-close]') || e.target.closest('[data-el-drawer-close]')) closeDrawer();
    });

    window.addEventListener('resize', setHeaderGroupHeight);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
